import React, { useRef, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBold,
  faItalic,
  faListUl,
  faListOl,
  faAlignLeft,
  faAlignCenter,
  faAlignRight,
  faUnderline
} from "@fortawesome/free-solid-svg-icons";

/**
 * Convert Markdown formatted text (README style) into clean HTML.
 */
export function markdownToHtml(text) {
  if (!text) return "";
  let md = text;

  // Check if text already has rich HTML markup
  const hasHtmlTags = /<(p|div|ul|ol|li|strong|b|em|i|h[1-6]|br)[^>]*>/i.test(md);
  const hasMarkdownPatterns = /^#{1,6}\s+|^\s*[-*]\s+|^\s*\d+\.\s+|\*\*|__|\*[^*]+\*|_[^_]+_/m.test(md);

  if (hasHtmlTags && !hasMarkdownPatterns) {
    return md;
  }

  // Headings
  md = md.replace(/^###[ \t]+(.*$)/gim, '<h4 style="font-weight:700;margin:12px 0 6px;color:#1e293b;">$1</h4>');
  md = md.replace(/^##[ \t]+(.*$)/gim, '<h3 style="font-weight:700;margin:14px 0 8px;color:#0f172a;">$1</h3>');
  md = md.replace(/^#[ \t]+(.*$)/gim, '<h2 style="font-weight:800;margin:16px 0 10px;color:#0f172a;">$1</h2>');

  // Bold & Italic
  md = md.replace(/\*\*\*(.*?)\*\*\*/g, '<strong><em>$1</em></strong>');
  md = md.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  md = md.replace(/\*(.*?)\*/g, '<em>$1</em>');
  md = md.replace(/___(.*?)___/g, '<strong><em>$1</em></strong>');
  md = md.replace(/__(.*?)__/g, '<strong>$1</strong>');
  md = md.replace(/_(.*?)_/g, '<em>$1</em>');

  // Blockquotes
  md = md.replace(/^>[ \t]+(.*$)/gim, '<blockquote style="border-left:3px solid #6366f1;padding-left:12px;margin:8px 0;color:#64748b;font-style:italic;">$1</blockquote>');

  // Unordered list items (- or *)
  md = md.replace(/(?:^|\n)[ \t]*[-*][ \t]+(.*)(?=(?:\n[ \t]*[-*][ \t]+.*)*)/g, function (match) {
    const lines = match.trim().split(/\n[ \t]*[-*][ \t]+/);
    const items = lines
      .map((l) => l.replace(/^[ \t]*[-*][ \t]+/, "").trim())
      .filter(Boolean);
    return "<ul>" + items.map((it) => `<li>${it}</li>`).join("") + "</ul>";
  });

  // Ordered list items (1. 2. etc)
  md = md.replace(/(?:^|\n)[ \t]*\d+\.[ \t]+(.*)(?=(?:\n[ \t]*\d+\.[ \t]+.*)*)/g, function (match) {
    const lines = match.trim().split(/\n[ \t]*\d+\.[ \t]+/);
    const items = lines
      .map((l) => l.replace(/^[ \t]*\d+\.[ \t]+/, "").trim())
      .filter(Boolean);
    return "<ol>" + items.map((it) => `<li>${it}</li>`).join("") + "</ol>";
  });

  // If plain text paragraphs without tags, handle newlines
  if (!hasHtmlTags) {
    const paragraphs = md.split(/\n{2,}/);
    md = paragraphs
      .map((p) => {
        const trimmed = p.trim();
        if (!trimmed) return "";
        if (/^<(h[1-6]|ul|ol|blockquote)/i.test(trimmed)) {
          return trimmed;
        }
        return `<p>${trimmed.replace(/\n/g, "<br/>")}</p>`;
      })
      .join("");
  }

  return md;
}

/**
 * RichTextEditor — A lightweight contentEditable-based rich text editor.
 * Provides Bold, Italic, Underline, Bullet Lists, Numbered Lists, and Alignment controls.
 * Supports Markdown/README formatted text pasting and HTML output.
 */
function RichTextEditor({ value, onChange, placeholder }) {
  const editorRef = useRef(null);
  const isInternalChange = useRef(false);

  // Sync external value into the editor only when value changes externally
  useEffect(() => {
    if (editorRef.current && !isInternalChange.current) {
      if (editorRef.current.innerHTML !== value) {
        editorRef.current.innerHTML = value || '';
      }
    }
    isInternalChange.current = false;
  }, [value]);

  const handleInput = useCallback(() => {
    isInternalChange.current = true;
    const html = editorRef.current ? editorRef.current.innerHTML : '';
    const cleaned = html.replace(/^(<div><br><\/div>|<br>)$/i, '');
    onChange(cleaned);
  }, [onChange]);

  const execCommand = (command, val = null) => {
    editorRef.current?.focus();
    document.execCommand(command, false, val);
    handleInput();
  };

  const isActive = (command) => {
    try {
      return document.queryCommandState(command);
    } catch {
      return false;
    }
  };

  // Toolbar button style helper
  const toolbarBtnStyle = (command) => ({
    background: isActive(command) ? 'rgba(79, 70, 229, 0.15)' : 'transparent',
    border: isActive(command) ? '1.5px solid #4f46e5' : '1.5px solid transparent',
    borderRadius: '8px',
    width: '36px',
    height: '36px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: isActive(command) ? '#4f46e5' : '#64748b',
    fontSize: '0.92rem',
    transition: 'all 0.15s ease',
  });

  const separatorStyle = {
    width: '1px',
    height: '24px',
    background: '#e2e8f0',
    margin: '0 4px',
    flexShrink: 0,
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const plainText = e.clipboardData.getData('text/plain') || '';
    const htmlText = e.clipboardData.getData('text/html') || '';

    // Check if pasted text contains markdown patterns (headings, bullets, bold, quotes)
    const hasMarkdown = /^#{1,6}\s+|^\s*[-*]\s+|^\s*\d+\.\s+|\*\*|__|\*[^*]+\*|_[^_]+_/m.test(plainText);

    if (hasMarkdown) {
      const converted = markdownToHtml(plainText);
      document.execCommand('insertHTML', false, converted);
    } else if (htmlText) {
      document.execCommand('insertHTML', false, htmlText);
    } else {
      document.execCommand('insertText', false, plainText);
    }
    handleInput();
  };

  const isEmpty = !value || value === '<br>' || value === '<div><br></div>';

  return (
    <div
      style={{
        border: '1.5px solid #cbd5e1',
        borderRadius: '12px',
        overflow: 'hidden',
        background: '#fff',
        transition: 'border-color 0.2s ease',
      }}
      onFocus={() => {
        const el = editorRef.current?.parentElement?.parentElement;
        if (el) el.style.borderColor = '#4f46e5';
      }}
      onBlur={() => {
        const el = editorRef.current?.parentElement?.parentElement;
        if (el) el.style.borderColor = '#cbd5e1';
      }}
    >
      {/* Toolbar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '3px',
          padding: '8px 12px',
          background: '#f8fafc',
          borderBottom: '1px solid #e2e8f0',
          flexWrap: 'wrap',
        }}
      >
        {/* Text Formatting */}
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); execCommand('bold'); }}
          style={toolbarBtnStyle('bold')}
          title="Bold (Ctrl+B)"
        >
          <FontAwesomeIcon icon={faBold} />
        </button>
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); execCommand('italic'); }}
          style={toolbarBtnStyle('italic')}
          title="Italic (Ctrl+I)"
        >
          <FontAwesomeIcon icon={faItalic} />
        </button>
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); execCommand('underline'); }}
          style={toolbarBtnStyle('underline')}
          title="Underline (Ctrl+U)"
        >
          <FontAwesomeIcon icon={faUnderline} />
        </button>

        <div style={separatorStyle}></div>

        {/* Bullet & Numbered Lists with focus preservation */}
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); execCommand('insertUnorderedList'); }}
          style={toolbarBtnStyle('insertUnorderedList')}
          title="Bullet List"
        >
          <FontAwesomeIcon icon={faListUl} />
        </button>
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); execCommand('insertOrderedList'); }}
          style={toolbarBtnStyle('insertOrderedList')}
          title="Numbered List"
        >
          <FontAwesomeIcon icon={faListOl} />
        </button>

        <div style={separatorStyle}></div>

        {/* Alignment */}
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); execCommand('justifyLeft'); }}
          style={toolbarBtnStyle('justifyLeft')}
          title="Align Left"
        >
          <FontAwesomeIcon icon={faAlignLeft} />
        </button>
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); execCommand('justifyCenter'); }}
          style={toolbarBtnStyle('justifyCenter')}
          title="Align Center"
        >
          <FontAwesomeIcon icon={faAlignCenter} />
        </button>
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); execCommand('justifyRight'); }}
          style={toolbarBtnStyle('justifyRight')}
          title="Align Right"
        >
          <FontAwesomeIcon icon={faAlignRight} />
        </button>

        <div style={{ marginLeft: 'auto', fontSize: '0.74rem', color: '#94a3b8', fontStyle: 'italic' }}>
          Supports Markdown / README paste
        </div>
      </div>

      {/* Editable Area */}
      <div style={{ position: 'relative' }}>
        {isEmpty && (
          <div
            style={{
              position: 'absolute',
              top: '14px',
              left: '16px',
              color: '#94a3b8',
              fontSize: '0.95rem',
              pointerEvents: 'none',
              userSelect: 'none',
            }}
          >
            {placeholder || 'Start typing or paste description (Markdown & formatting supported)...'}
          </div>
        )}
        <div
          ref={editorRef}
          className="rich-text-editor-area"
          contentEditable
          suppressContentEditableWarning
          onInput={handleInput}
          onPaste={handlePaste}
        />
      </div>
    </div>
  );
}

export default RichTextEditor;
